/**
 * UploadJobContext — app-level progress + knowledge-impact tracker for a
 * document upload (Browse Device / Google Drive / Dropbox). It backs a
 * persistent, unobtrusive floating <UploadProgressChip> so the user is never
 * left wondering whether an upload started, and is never trapped behind a
 * blocking overlay or an abrupt full-screen "Knowledge Updated" modal.
 *
 * Two things are tracked for the active job:
 *  • Progress — the upload call site registers each created document id; we tick
 *    `processed`/`failed` off the documents Ably channel (useDocumentChannel),
 *    exactly like FolderScanJobContext.
 *  • Impact — we ALSO subscribe to the ingest channel ('Ingestion Event') and
 *    accumulate each file's summary + extraction counts LIVE (no 30s debounce),
 *    so tapping the chip opens the IngestionImpactSheet populated immediately.
 *
 * One job at a time; starting a new upload supersedes the previous (a seq guard
 * invalidates stale callbacks). Mounted inside AblyProvider (see App.tsx).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDocumentChannel } from '../hooks/useDocumentChannel';
import { useIngestionProgressFeed, type IngestionProgressEvent } from '../hooks/useIngestionProgressFeed';
import { useAblyClient } from '../contexts/AblyContext';
import config from '../config';
import type { RootState } from '../store';
import type { IngestionImpactSheetData, IngestionImpactItem } from '../hooks/useIngestionImpactFeed';
import { apiGet } from '../utilities/apiClient';
import { computeIngestFraction, slowestStage, type IngestStage } from '../utilities/uploadProgress';

export type UploadJobStatus = 'uploading' | 'ingesting' | 'delayed' | 'done';

export type UploadJobFile = {
  name: string;
  documentId?: string;
  status: 'uploading' | 'processing' | 'ready' | 'failed' | 'kept';
  replaced?: boolean;
  stage?: IngestStage;
};

export interface UploadJob {
  projectId: number | string;
  projectName: string;
  /** 'upload' = a user-initiated file upload (progress → impact). 'passive' =
   *  background ingestion (email inbox, Fasten, …) surfaced without an in-app
   *  upload; it goes straight to a done/knowledge-updated state. */
  kind: 'upload' | 'passive';
  /** Files in this batch (grows if more are added before it finishes). */
  total: number;
  /** Files PUT to S3 + document record created. */
  uploaded: number;
  /** Documents that finished backend processing (or were 'active' on create). */
  processed: number;
  /** Files that failed to upload OR failed processing. */
  failed: number;
  status: UploadJobStatus;
  files?: UploadJobFile[];
  skipped?: number;
}

interface ImpactItem {
  /** The storage key — lookups only. */
  filename: string;
  /** What to call it on screen. */
  displayName: string;
  summary: string;
  medications: number;
  contacts: number;
  events: number;
  inferences: number;
  memories: number;
}

interface UploadJobContextValue {
  job: UploadJob | null;
  impact: IngestionImpactSheetData | null;
  /** Live "reading" progress in [0,1] while ingesting (driven by per-doc stage
   *  events), or null when not ingesting / no stage data yet. */
  ingestFraction: number | null;
  /** Furthest-behind stage across in-flight docs (the laggard), for labelling. */
  ingestStage: IngestStage | null;
  /** Begin a job for `total` files (call the instant the picker returns files). */
  start: (projectId: number | string, projectName: string, total: number, filenames?: string[]) => void;
  /** Surface a passive (non-upload) ingestion result on the chip — e.g. files
   *  that arrived via the email inbox or a Fasten sync. No-op while a
   *  user-initiated upload owns the surface. */
  surfacePassive: (projectId: number | string, data: IngestionImpactSheetData) => void;
  /** A file was successfully uploaded; pass the created document id (and its
   *  create status, if known) to track it. */
  registerUploaded: (documentId: string | number | null | undefined, status?: string, file?: { index: number; replaced?: boolean }) => void;
  /** A file failed to upload. */
  registerFailed: (file?: { index: number; skipped?: boolean }) => void;
  /** Every registered document is readable; includes fast/early events and existing rows. */
  textReady: boolean;
  /** The upload loop has issued every file. */
  finishUploading: () => void;
  dismiss: () => void;
}

const UploadJobContext = createContext<UploadJobContextValue>({
  job: null,
  impact: null,
  ingestFraction: null,
  ingestStage: null,
  textReady: false,
  start: () => {},
  surfacePassive: () => {},
  registerUploaded: () => {},
  registerFailed: () => {},
  finishUploading: () => {},
  dismiss: () => {},
});

export const useUploadJob = () => useContext(UploadJobContext);

/** Don't let a missed processing/ingest event spin the chip forever. */
const INGEST_FALLBACK_MS = 4 * 60 * 1000;
const RECONCILE_INTERVAL_MS = 5 * 1000;

function toCount(v: any): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
}

export function UploadJobProvider({ children }: { children: React.ReactNode }) {
  const { client } = useAblyClient();
  const account = useSelector((s: RootState) => (s as any).session?.account);
  const user = useSelector((s: RootState) => (s as any).authentication?.user);

  const [job, setJob] = useState<UploadJob | null>(null);
  const [impact, setImpact] = useState<IngestionImpactSheetData | null>(null);

  const pendingIdsRef = useRef<Set<string>>(new Set());   // uploaded, awaiting completion
  const countedIdsRef = useRef<Set<string>>(new Set());   // already counted (dedupe rewind)
  const registeredIdsRef = useRef<Set<string>>(new Set());
  const settledOutcomeByIdRef = useRef<Map<string, 'processed' | 'failed'>>(new Map());
  const documentEpochByIdRef = useRef<Map<string, number>>(new Map());
  // A replacement upload can reuse the same document id. Its terminal state
  // must come from the row after the replacement response—not an older Ably
  // completion for the previous bytes.
  const reconcileOnlyIdsRef = useRef<Set<string>>(new Set());
  const impactSuppressedIdsRef = useRef<Set<string>>(new Set());
  // A fast processor can publish before /documents/create returns the id to
  // the caller. Retain those events for this job and drain them on registration.
  const earlyTerminalByIdRef = useRef<Map<string, { doc: any; timestamp?: number }>>(new Map());
  const earlyStageByIdRef = useRef<Map<string, IngestStage>>(new Map());
  const uploadDoneRef = useRef(false);
  const seqRef = useRef(0);
  const jobStartedAtRef = useRef(0);
  const reconcileInFlightRef = useRef<number | null>(null);
  const impactRef = useRef<Map<string, ImpactItem>>(new Map());
  const stageByDocRef = useRef<Map<string, IngestStage>>(new Map()); // furthest read-stage per pending doc
  const [ingestTick, setIngestTick] = useState(0);                    // bumps on each stage event → recompute progress

  const rebuildImpact = useCallback(() => {
    // The project feed may also carry an email/Fasten import or another member's
    // upload. Only attribute knowledge impact to documents in this upload job.
    const entries = Array.from(impactRef.current.entries())
      .filter(([documentId]) => registeredIdsRef.current.has(documentId));
    if (!entries.length) {
      setImpact(null);
      return;
    }
    const items: IngestionImpactItem[] = entries.map(([documentId, it]) => ({
      documentId,
      filename: it.filename,
      displayName: it.displayName || it.filename,
      summary: it.summary,
      medications: it.medications,
      contacts: it.contacts,
      events: it.events,
      inferences: it.inferences,
      memories: it.memories,
    }));
    const totals = items.reduce(
      (a, it) => {
        a.contacts += it.contacts;
        a.medications += it.medications;
        a.events += it.events;
        a.inferences += it.inferences;
        a.memories += it.memories;
        return a;
      },
      { contacts: 0, medications: 0, events: 0, inferences: 0, memories: 0 },
    );
    setImpact({
      fileCount: items.length,
      ...totals,
      items,
    });
  }, []);

  const resetRefs = useCallback(() => {
    pendingIdsRef.current = new Set();
    countedIdsRef.current = new Set();
    registeredIdsRef.current = new Set();
    settledOutcomeByIdRef.current = new Map();
    documentEpochByIdRef.current = new Map();
    reconcileOnlyIdsRef.current = new Set();
    impactSuppressedIdsRef.current = new Set();
    earlyTerminalByIdRef.current = new Map();
    earlyStageByIdRef.current = new Map();
    impactRef.current = new Map();
    stageByDocRef.current = new Map();
    uploadDoneRef.current = false;
    reconcileInFlightRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    seqRef.current++;
    resetRefs();
    setImpact(null);
    setJob(null);
  }, [resetRefs]);

  const start = useCallback((projectId: number | string, projectName: string, total: number, filenames?: string[]) => {
    seqRef.current++;
    resetRefs();
    jobStartedAtRef.current = Date.now();
    setImpact(null);
    setJob({
      projectId,
      projectName: projectName || '',
      kind: 'upload',
      total: Math.max(0, total),
      uploaded: 0,
      processed: 0,
      failed: 0,
      status: 'uploading',
      ...(filenames ? { files: filenames.map((name) => ({ name, status: 'uploading' as const })) } : {}),
    });
  }, [resetRefs]);

  // Background ingestion (email inbox, Fasten, …): no upload progress to track,
  // so jump straight to a done/knowledge-updated chip. Ignored while a
  // user-initiated upload owns the surface — that job's own ingest subscription
  // already accumulates its impact.
  const surfacePassive = useCallback((projectId: number | string, data: IngestionImpactSheetData) => {
    if (job && job.kind === 'upload') return;
    setImpact(data);
    setJob({
      projectId,
      projectName: '',
      kind: 'passive',
      total: data.fileCount,
      uploaded: data.fileCount,
      processed: data.fileCount,
      failed: 0,
      status: 'done',
    });
  }, [job]);

  const settlePendingDocument = useCallback((doc: any) => {
    const id = doc?.id != null ? String(doc.id) : null;
    if (!id || !pendingIdsRef.current.has(id) || countedIdsRef.current.has(id)) return false;
    const succeeded = doc?.status === 'active';
    const failed = !!doc?.failure_code || doc?.status === 'failed';
    if (!succeeded && !failed) return false;

    countedIdsRef.current.add(id);
    settledOutcomeByIdRef.current.set(id, succeeded ? 'processed' : 'failed');
    pendingIdsRef.current.delete(id);
    reconcileOnlyIdsRef.current.delete(id);
    stageByDocRef.current.delete(id);
    setJob((j) =>
      j
        ? succeeded
          ? { ...j, processed: j.processed + 1, ...(j.files ? { files: j.files.map((f) => f.documentId === id ? { ...f, status: 'ready' as const } : f) } : {}) }
          : { ...j, failed: j.failed + 1, ...(j.files ? { files: j.files.map((f) => f.documentId === id ? { ...f, status: 'failed' as const } : f) } : {}) }
        : j,
    );
    return true;
  }, []);

  const recordStage = useCallback((id: string, stage: IngestStage) => {
    if (!pendingIdsRef.current.has(id) || countedIdsRef.current.has(id)) return;
    const prev = stageByDocRef.current.get(id);
    // Never regress a doc's stage — events can arrive out of order / duplicated.
    if (prev === 'findings' || (prev === 'extracted' && stage === 'extracted')) return;
    stageByDocRef.current.set(id, stage);
    setJob((j) => j?.files ? { ...j, files: j.files.map((f) => f.documentId === id ? { ...f, stage } : f) } : j);
    setIngestTick((t) => t + 1);
  }, []);

  const registerUploaded = useCallback((documentId: string | number | null | undefined, status?: string, file?: { index: number; replaced?: boolean }) => {
    const id = documentId != null ? String(documentId) : null;
    if (file) setJob((j) => j?.files ? { ...j, files: j.files.map((f, index) => index === file.index
      ? { ...f, documentId: id ?? undefined, replaced: file.replaced, status: !id ? 'failed' : status === 'active' ? 'ready' : 'processing' }
      : f) } : j);
    // A create response without a usable identity cannot ever be reconciled.
    // Count the attempt as failed instead of manufacturing a permanent pending
    // item at the progress bar's base 8%.
    if (!id) {
      setJob((j) => (j ? { ...j, failed: j.failed + 1 } : j));
      return;
    }

    // Duplicate filenames resolve to the same server document id. A duplicate
    // create can also replace and requeue that row, so reopen it and reconcile
    // the final row instead of treating the first generation as terminal.
    if (registeredIdsRef.current.has(id)) {
      documentEpochByIdRef.current.set(id, (documentEpochByIdRef.current.get(id) || 1) + 1);
      const priorOutcome = settledOutcomeByIdRef.current.get(id);
      countedIdsRef.current.delete(id);
      settledOutcomeByIdRef.current.delete(id);
      pendingIdsRef.current.delete(id);
      stageByDocRef.current.delete(id);
      earlyTerminalByIdRef.current.delete(id);
      earlyStageByIdRef.current.delete(id);
      impactRef.current.delete(id);
      impactSuppressedIdsRef.current.add(id);

      const isActive = status === 'active';
      if (isActive) {
        countedIdsRef.current.add(id);
        settledOutcomeByIdRef.current.set(id, 'processed');
        reconcileOnlyIdsRef.current.delete(id);
      } else {
        pendingIdsRef.current.add(id);
        reconcileOnlyIdsRef.current.add(id);
      }

      setJob((j) => {
        if (!j) return j;
        let processed = Math.max(0, j.processed - (priorOutcome === 'processed' ? 1 : 0));
        const failed = Math.max(0, j.failed - (priorOutcome === 'failed' ? 1 : 0));
        if (isActive) processed += 1;
        const total = Math.max(registeredIdsRef.current.size, processed + failed, j.total - 1);
        return {
          ...j,
          total,
          processed,
          failed,
          ...(j.files ? { files: j.files.map((f) => f.documentId === id
            ? { ...f, status: isActive ? 'ready' as const : 'processing' as const, stage: undefined } : f) } : {}),
          status: isActive
            ? (uploadDoneRef.current && processed + failed >= total ? 'done' : j.status)
            : (uploadDoneRef.current ? 'ingesting' : j.status),
        };
      });
      rebuildImpact();
      return;
    }
    registeredIdsRef.current.add(id);
    documentEpochByIdRef.current.set(id, 1);

    // A doc created 'active' (rare fast-path types) gets no Ably completion, so
    // count it processed now; otherwise track it as pending until the documents
    // channel or reconciliation reports completion.
    if (status === 'active') {
      countedIdsRef.current.add(id);
      settledOutcomeByIdRef.current.set(id, 'processed');
      earlyTerminalByIdRef.current.delete(id);
      earlyStageByIdRef.current.delete(id);
      setJob((j) => (j ? { ...j, uploaded: j.uploaded + 1, processed: j.processed + 1 } : j));
      rebuildImpact();
      return;
    }

    pendingIdsRef.current.add(id);
    setJob((j) => (j ? { ...j, uploaded: j.uploaded + 1 } : j));

    const earlyStage = earlyStageByIdRef.current.get(id);
    earlyStageByIdRef.current.delete(id);
    if (earlyStage) recordStage(id, earlyStage);

    const earlyTerminal = earlyTerminalByIdRef.current.get(id);
    earlyTerminalByIdRef.current.delete(id);
    if (earlyTerminal) settlePendingDocument(earlyTerminal.doc);
    rebuildImpact();
  }, [rebuildImpact, recordStage, settlePendingDocument]);

  const registerFailed = useCallback((file?: { index: number; skipped?: boolean }) => {
    setJob((j) => (j ? {
      ...j,
      failed: j.failed + (file?.skipped ? 0 : 1),
      ...(file?.skipped ? { skipped: (j.skipped ?? 0) + 1 } : {}),
      ...(j.files && file ? { files: j.files.map((f, index) => index === file.index ? { ...f, status: file.skipped ? 'kept' as const : 'failed' as const } : f) } : {}),
    } : j));
  }, []);

  const finishUploading = useCallback(() => {
    uploadDoneRef.current = true;
    setJob((j) => {
      if (!j) return j;
      // If a call site aborts its loop on an exception, attempts that never
      // reached registerUploaded/registerFailed are failed uploads—not an
      // unknowable backend processing state.
      const missingAttempts = Math.max(0, j.total - j.uploaded - j.failed - (j.skipped ?? 0));
      const failed = j.failed + missingAttempts;
      return {
        ...j,
        failed,
        ...(j.files ? { files: j.files.map((f) => f.status === 'uploading' ? { ...f, status: 'failed' as const } : f) } : {}),
        status: pendingIdsRef.current.size > 0
          ? 'ingesting'
          : (j.processed + failed + (j.skipped ?? 0) >= j.total ? 'done' : 'ingesting'),
      };
    });
  }, []);

  // ── Processing completions via the documents Ably channel (ticks `processed`) ──
  const handleDocEvent = useCallback((doc: any, meta?: { timestamp?: number }) => {
    const id = doc?.id != null ? String(doc.id) : null;
    if (!id || countedIdsRef.current.has(id)) return;
    if (reconcileOnlyIdsRef.current.has(id)) return;
    const succeeded = doc?.status === 'active';
    const failed = !!doc?.failure_code || doc?.status === 'failed';
    if (!succeeded && !failed) return; // intermediate update

    // rewind:1 may replay a terminal event from before this batch, especially
    // when replacing a document that reuses an id. Apply the job boundary even
    // after the create response has registered that id.
    if (meta?.timestamp != null && meta.timestamp < jobStartedAtRef.current) return;
    if (pendingIdsRef.current.has(id)) {
      settlePendingDocument(doc);
      return;
    }

    earlyTerminalByIdRef.current.set(id, { doc, timestamp: meta?.timestamp });
  }, [settlePendingDocument]);

  const reconcilePending = useCallback(async () => {
    if (
      reconcileInFlightRef.current != null
      || job?.kind !== 'upload'
      || job?.projectId == null
      || pendingIdsRef.current.size === 0
      || !account?.account_id
      || !user?.cognito_id
    ) return;

    const generation = seqRef.current;
    const pendingIds = Array.from(pendingIdsRef.current);
    const pendingEpochs = new Map(
      pendingIds.map((id) => [id, documentEpochByIdRef.current.get(id) || 0]),
    );
    reconcileInFlightRef.current = generation;
    try {
      const response = await apiGet(
        `/documents/get-all/${job.projectId}/${account.account_id}/${user.cognito_id}?reconcile_ids=${pendingIds.map(encodeURIComponent).join(',')}`,
      );
      if (generation !== seqRef.current || response?.success === false || !Array.isArray(response?.payload)) return;
      response.payload.forEach((doc: any) => {
        const id = doc?.id != null ? String(doc.id) : null;
        if (
          id
          && pendingIdsRef.current.has(id)
          && documentEpochByIdRef.current.get(id) === pendingEpochs.get(id)
        ) settlePendingDocument(doc);
      });
    } catch {
      // A transient status-read failure leaves the job pending. The bounded
      // interval will try again; uncertainty is not a document failure.
    } finally {
      if (reconcileInFlightRef.current === generation) {
        reconcileInFlightRef.current = null;
      }
    }
  }, [account?.account_id, user?.cognito_id, job?.kind, job?.projectId, settlePendingDocument]);

  useDocumentChannel({
    projectId: job?.projectId ?? null,
    onMessage: handleDocEvent,
    onReconcile: reconcilePending,
  });

  // ── Per-stage read progress via the ingest channel ('Ingestion Progress') ──
  // Backend publishes 'extracted' then 'findings' per document as it reads it;
  // tracking the furthest stage of each in-flight doc lets the chip show a moving
  // bar instead of a flat 0% spinner. Upload jobs only — a null projectId keeps
  // the subscription off for passive jobs (ED-2496).
  const handleIngestProgress = useCallback((ev: IngestionProgressEvent) => {
    const id = ev?.document_id != null ? String(ev.document_id) : null;
    if (!id || countedIdsRef.current.has(id)) return;
    if (reconcileOnlyIdsRef.current.has(id)) return;
    if (ev.stage !== 'extracted' && ev.stage !== 'findings') return;
    if (ev.stage === 'extracted' && Number(ev.char_count || 0) <= 0) return;
    if (pendingIdsRef.current.has(id)) {
      recordStage(id, ev.stage);
      return;
    }
    const prev = earlyStageByIdRef.current.get(id);
    if (prev !== 'findings') earlyStageByIdRef.current.set(id, ev.stage);
  }, [recordStage]);

  useIngestionProgressFeed({
    projectId: job?.kind === 'upload' ? (job?.projectId ?? null) : null,
    onEvent: handleIngestProgress,
  });

  // Live "reading" fraction + slowest stage, derived from the per-doc stage map.
  // Recomputed when a stage lands (ingestTick) or a doc is added/completed.
  const ingestFraction = useMemo(() => {
    if (!job || job.kind !== 'upload' || job.status !== 'ingesting') return null;
    const pending = Array.from(pendingIdsRef.current, (docId) => stageByDocRef.current.get(docId));
    return computeIngestFraction(job.total, job.processed + job.failed, pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.kind, job?.status, job?.total, job?.uploaded, job?.processed, job?.failed, ingestTick]);

  const ingestStage = useMemo(() => {
    if (!job || job.status !== 'ingesting') return null;
    const pending = Array.from(pendingIdsRef.current, (docId) => stageByDocRef.current.get(docId));
    return slowestStage(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status, job?.uploaded, job?.processed, job?.failed, ingestTick]);

  const textReady = !!job && job.kind === 'upload' && uploadDoneRef.current
    && job.processed + pendingIdsRef.current.size > 0
    && Array.from(pendingIdsRef.current).every((id) => stageByDocRef.current.has(id));

  // ── Knowledge impact via the ingest Ably channel (accumulated LIVE) ──
  // Keyed on the job's projectId only, so it subscribes once per job and is NOT
  // torn down/re-created on every progress tick.
  useEffect(() => {
    const pid = job?.projectId;
    // Only an active upload job accumulates impact here; passive ingestion is
    // fed in via surfacePassive() (the per-screen useIngestionImpactFeed owns
    // that subscription), so we don't double-subscribe for passive jobs.
    if (!client || pid == null || job?.kind !== 'upload' || !account?.account_id || !user?.cognito_id) return;
    const channelName = `ingest_${config.env}_${account.account_id}_${user.cognito_id}_${pid}`;
    const eventName = 'Ingestion Event';
    let channel: any;
    const handler = (message: any) => {
      const results: any[] = message?.data?.results;
      if (!Array.isArray(results) || results.length === 0) return;
      let changed = false;
      results.forEach((r) => {
        if (r?.status !== 'processed_file' || r?.document_id == null) return;
        const id = String(r.document_id);
        // Replacement uploads reuse an id and current ingest events carry no
        // revision. Suppress impact for that id rather than merging learnings
        // from the old and new bytes into a misleading result.
        if (impactSuppressedIdsRef.current.has(id)) return;
        const existing = impactRef.current.get(id);
        const incomingSummary = (r.summary || '').trim();
        const summary = (existing?.summary || '').length >= incomingSummary.length ? (existing?.summary || '') : incomingSummary;
        const filename = r.filename || existing?.filename || 'Document';
        impactRef.current.set(id, {
          filename,
          // `filename` is the storage key; `display_name` is what to call it.
          // Older events don't carry one, so fall back.
          displayName: (r.display_name || '').trim() || existing?.displayName || filename,
          summary,
          medications: Math.max(existing?.medications || 0, toCount(r.medications)),
          contacts: Math.max(existing?.contacts || 0, toCount(r.contacts)),
          events: Math.max(existing?.events || 0, toCount(r.events)),
          inferences: Math.max(existing?.inferences || 0, toCount(r.inferences)),
          memories: Math.max(existing?.memories || 0, toCount(r.memories)),
        });
        changed = true;
      });
      if (changed) rebuildImpact();
    };
    try {
      channel = client.channels.get(channelName);
      channel.subscribe(eventName, handler);
    } catch {
      // Ably may not be ready; effect re-runs when deps change.
    }
    return () => {
      try { channel?.unsubscribe(eventName, handler); } catch {}
    };
  }, [client, job?.projectId, job?.kind, account?.account_id, user?.cognito_id, rebuildImpact]);

  // Flip to 'done' once uploads are issued and every doc is accounted for.
  useEffect(() => {
    if (!job || job.status === 'done') return;
    if (uploadDoneRef.current && job.processed + job.failed + (job.skipped ?? 0) >= job.total) {
      setJob((j) => (j && j.status !== 'done' ? { ...j, status: 'done' } : j));
    }
  }, [job]);

  // Reconcile from authoritative rows as well as realtime. This covers a
  // completion published while Ably was detached and also runs when the
  // channel attaches via useDocumentChannel.onReconcile above.
  useEffect(() => {
    if (job?.status !== 'ingesting') return;
    const generation = seqRef.current;
    void reconcilePending();
    const interval = setInterval(() => {
      if (generation !== seqRef.current) return;
      void reconcilePending();
    }, RECONCILE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [job?.status, reconcilePending]);

  // A timeout is uncertainty, not success. Stop the spinner and give the user
  // truthful background-processing copy without changing terminal counts.
  useEffect(() => {
    if (job?.status !== 'ingesting') return;
    const generation = seqRef.current;
    const t = setTimeout(() => {
      if (generation !== seqRef.current) return;
      setJob((j) => (j && j.status === 'ingesting' ? { ...j, status: 'delayed' } : j));
    }, INGEST_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [job?.status]);

  // `findings` means the expensive work is near completion, but it is not an
  // authoritative success signal. Reconcile immediately instead of fabricating
  // processed counts after a grace timer.
  useEffect(() => {
    if (!job || job.kind !== 'upload' || job.status !== 'ingesting' || !uploadDoneRef.current) return;
    const pending = Array.from(pendingIdsRef.current, (docId) => stageByDocRef.current.get(docId));
    if (pending.length === 0 || !pending.every((stage) => stage === 'findings')) return;
    void reconcilePending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.kind, job?.status, job?.uploaded, job?.processed, job?.failed, ingestTick, reconcilePending]);

  return (
    <UploadJobContext.Provider value={{ job, impact, ingestFraction, ingestStage, textReady, start, surfacePassive, registerUploaded, registerFailed, finishUploading, dismiss }}>
      {children}
    </UploadJobContext.Provider>
  );
}
